import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { _socials } from 'src/_mock';
import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

const DOWNLOAD_APP = [
  {
    marging: 1.5,
    text: 'Available on',
    platform: 'Google Play',
    link: CONFIG.googlePlay,
    icon: <Iconify icon="logos:google-play-icon" width={25} />,
  },
  {
    marging: 1.2,
    text: 'Get it on',
    platform: 'App Store',
    link: CONFIG.appleStore,
    icon: <Iconify icon="fa6-brands:apple" width={30} sx={{ color: 'common.dark' }} />,
  },
];

const FooterRoot = styled('footer')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.vars.palette.background.paper,
}));

export function FooterDesktop({ sx, layoutQuery = 'md', ...other }) {
  const { t, currentLang } = useTranslate();

  const search = newSearchParams({ country: currentLang.countryName });

  const LINKS = [
    {
      headline: 'Info',
      children: [
        {
          name: `About ${CONFIG.appName}`,
          icon: 'flat-color-icons:info',
          href: `${paths.about}${search}`,
        },
        {
          name: 'FAQs',
          icon: 'fluent-color:chat-bubbles-question-24',
          href: `${paths.faqs}${search}`,
        },
      ],
    },
    {
      headline: 'Join up',
      children: [
        {
          name: 'Start selling now',
          icon: 'fluent-color:building-store-24',
          href: `${paths.product.new}${search}`,
        },
        {
          name: 'Be an eDriver',
          icon: 'flat-color-icons:in-transit',
          href: 'https://medriver.app',
        },
      ],
    },
    {
      headline: 'Legal',
      children: [
        {
          name: 'Terms of service',
          icon: 'fluent-color:receipt-32',
          href: `${paths.legal.terms}${search}`,
          target: '_blank',
        },
        {
          name: 'Privacy policy',
          icon: 'fluent-color:shield-checkmark-48',
          href: `${paths.legal.privacy}${search}`,
          target: '_blank',
        },
      ],
    },
    {
      headline: 'Contact',
      children: [
        {
          name: CONFIG.appInvoice.email,
          icon: 'fluent-color:mail-48',
          href: `mailto:${CONFIG.appInvoice.email}`,
          target: '_blank',
        },
        {
          name: 'See more',
          icon: 'solar:menu-dots-circle-line-duotone',
          href: `${paths.contact}${search}`,
        },
      ],
    },
  ];

  return (
    <FooterRoot sx={[...(Array.isArray(sx) ? sx : [sx])]} {...other}>
      <Stack
        sx={{
          backgroundColor: 'background.neutral',
          display: { xs: 'none', [layoutQuery]: 'block' },
        }}
      >
        <Divider sx={{ borderStyle: 'dashed' }} />

        <Container sx={{ py: 8 }}>
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ mb: 2, fontWeight: '600' }}>
              {t('Download the app on your cell phone!')}
            </Typography>

            <Stack spacing={4} direction="row">
              {DOWNLOAD_APP.map((button, index) => (
                <Button
                  key={index}
                  rel="noopener"
                  target="_blank"
                  href={button.link}
                  variant="contained"
                  sx={{ pl: 0, py: 1, pr: 2 }}
                >
                  <Stack alignItems="center" sx={{ mx: button.marging }}>
                    {button.icon}
                  </Stack>

                  <ListItemText
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      flexDirection: 'column-reverse',
                    }}
                    primary={button.platform}
                    secondary={t(button.text)}
                    slotProps={{
                      primary: { variant: 'body2' },
                      secondary: { variant: 'caption', color: 'text.disabled' },
                    }}
                  />
                </Button>
              ))}
            </Stack>
          </Stack>
        </Container>

        <Divider sx={{ borderStyle: { xs: 'dashed', [layoutQuery]: 'solid' } }} />
      </Stack>

      <Container
        sx={(theme) => ({
          p: 3,
          textAlign: 'center',
          [theme.breakpoints.up(layoutQuery)]: {
            px: 2,
            py: 7,
            textAlign: 'unset',
          },
        })}
      >
        <Grid container sx={{ justifyContent: { xs: 'center', [layoutQuery]: 'space-between' } }}>
          <Logo sx={{ display: { xs: 'none', [layoutQuery]: 'inline-flex' } }} />

          <Grid size={{ xs: 12, [layoutQuery]: 8 }}>
            <Stack spacing={4} sx={{ flexDirection: { xs: 'column', [layoutQuery]: 'row' } }}>
              {LINKS.map((list) => (
                <Stack
                  key={list.headline}
                  spacing={2}
                  sx={{
                    width: 1,
                    alignItems: { xs: 'start', [layoutQuery]: 'flex-start' },
                  }}
                >
                  <Typography component="div" variant="overline">
                    {t(list.headline)}
                  </Typography>

                  {list.children.map((link) => (
                    <Link
                      color="inherit"
                      key={link.name}
                      variant="body2"
                      href={link.href}
                      component={RouterLink}
                    >
                      <Stack spacing={1} direction="row" alignItems="center">
                        {link.icon && (
                          <Iconify
                            width={22}
                            icon={link.icon}
                            sx={{ display: { [layoutQuery]: 'none' } }}
                          />
                        )}

                        {t(link.name)}
                      </Stack>
                    </Link>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Divider />

      <Container
        sx={{
          p: { xs: 4, [layoutQuery]: 2 },
          textAlign: { xs: 'center', [layoutQuery]: 'unset' },
        }}
      >
        <Grid
          container
          sx={{
            alignItems: 'center',
            flexFlow: 'wrap-reverse',
            mt: { xs: 2, [layoutQuery]: 0 },
            gap: { xs: 2.5, [layoutQuery]: 0 },
            justifyContent: { xs: 'center', [layoutQuery]: 'space-between' },
          }}
        >
          <Grid size={{ xs: 12, [layoutQuery]: 6 }}>
            <Box
              sx={{
                mt: 1,
                typography: 'caption',
                gap: { xs: 1, [layoutQuery]: 0.5 },
                display: { xs: 'grid', [layoutQuery]: 'flex' },
              }}
            >
              <div>
                © {new Date().getFullYear()} {CONFIG.appInvoice.name} v{CONFIG.appVersion}.
              </div>
              <div>{t('All rights reserved')}</div>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, [layoutQuery]: 6 }}>
            <Stack direction="row" justifyContent={{ xs: 'center', [layoutQuery]: 'flex-end' }}>
              {_socials.map(
                (social) =>
                  social.user && (
                    <IconButton
                      key={social.name}
                      rel="noopener"
                      target="_blank"
                      color="text.secondary"
                      area-label={social.name}
                      href={`${social.path}${social.user}`}
                    >
                      <Iconify icon={social.icon} width={18} />
                    </IconButton>
                  )
              )}
            </Stack>
          </Grid>

          <Logo sx={{ display: { [layoutQuery]: 'none' } }} />
        </Grid>
      </Container>
    </FooterRoot>
  );
}
